import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { Grid, Checkbox } from '@mui/material';
import { Autocomplete, TextField, IconButton } from '@totalsoft/rocket-ui';
import { Close, Check } from '@mui/icons-material';
import { over, get, set } from '@totalsoft/change-tracking-react';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { emptyString } from 'utils/constants';
import { StyledTableRow, StyledTableBodyCell } from 'components/common/Styles';


const FinancialProductRulesEditDetailsServicesTableRow = ({
  canWrite,
  financialProductRuleServiceTypeList,
  financialProductRuleServiceDetailCalcTypeList,
  serviceLens,
  index,
  onCloseEdit,
  onDelete,
  onSave,
  onLoadFinancialProductRuleSupplierList,
  validation,
  listValid
}) => {

  const [t] = useTranslation();
  const service = serviceLens |> get;
  const { cvsChAmount } = useFieldDecimalPlacesConfig();

  return (
    <StyledTableRow>
      {(serviceLens.isInEditing |> get) ? (
        <>
          <StyledTableBodyCell>
            <Checkbox
              checked={(serviceLens.readOnlyYN |> get)}
              onChange={() => set(serviceLens.readOnlyYN)(!(serviceLens.readOnlyYN |> get))}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell sx={{ w: 1 / 4 }} >
            <Autocomplete
              fullWidth
              valueKey={'serviceTypeId'}
              labelKey={'serviceTypeName'}
              value={{ serviceTypeId: service.serviceTypeId, serviceTypeName: service.serviceTypeName }}
              options={financialProductRuleServiceTypeList}
              isSearchable={true}
              onChange={event => {
                over(serviceLens, oldState => ({
                  ...oldState,
                  serviceTypeId: event.serviceTypeId,
                  serviceTypeName: event.serviceTypeName
                }));
              }}
              error={!isValid(validation.serviceTypeId)}
              helperText={getErrors(validation.serviceTypeId)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <Autocomplete
              valueKey={'serviceDetailCalcTypeId'}
              labelKey={'serviceDetailCalcTypeName'}
              value={{ serviceDetailCalcTypeId: service.calcTypeId, serviceDetailCalcTypeName: service.calcTypeName }}
              options={financialProductRuleServiceDetailCalcTypeList}
              onChange={event => {
                over(serviceLens, oldState => ({
                  ...oldState,
                  calcTypeId: event.serviceDetailCalcTypeId,
                  calcTypeName: event.serviceDetailCalcTypeName
                }));
              }}
              error={!isValid(validation.calcTypeId)}
              helperText={getErrors(validation.calcTypeId)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <Autocomplete
              valueKey={'supplierId'}
              labelKey={'supplierName'}
              value={{ supplierId: service.supplierId, supplierName: service.supplierName }}
              onChange={event => {
                over(serviceLens, oldState => ({
                  ...oldState,
                  supplierId: event.supplierId,
                  supplierName: event.supplierName
                }));
              }}

              loadOptions={onLoadFinancialProductRuleSupplierList}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <TextField
              isNumeric
              fullWidth
              required
              value={service.percentOrValue || emptyString}
              onChange={serviceLens.percentOrValue |> set}
              error={!isValid(validation.percentOrValue)}
              helperText={getErrors(validation.percentOrValue)}
              decimalScale={cvsChAmount}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <TextField
              isNumeric
              fullWidth
              required
              value={service.periodicity || emptyString}
              onChange={serviceLens.periodicity |> set}
              error={!isValid(validation.periodicity)}
              helperText={getErrors(validation.periodicity)}
              decimalScale={0}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <Checkbox
              checked={(serviceLens.financedYN |> get)}
              onChange={() => set(serviceLens.financedYN)(!(serviceLens.financedYN |> get))}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <Checkbox
              checked={(serviceLens.implicit |> get)}
              onChange={() => set(serviceLens.implicit)(!(serviceLens.implicit |> get))}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <Checkbox
              checked={(serviceLens.mandatory |> get)}
              onChange={() => set(serviceLens.mandatory)(!(serviceLens.mandatory |> get))}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell align='right'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton
                type='save'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Save')}
                disabled={service.isSaving || !canWrite || !listValid}
                onClick={() => onSave(index)}
              />
              <IconButton
                type='cancel'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.CloseEdit')}
                onClick={() => onCloseEdit(index)}
              />
            </Grid>
          </StyledTableBodyCell>
        </>
      ) : (
        <>
          <StyledTableBodyCell>
            <>{(service.readOnlyYN) === true ? <Check color='success' /> : <Close />}</>
          </StyledTableBodyCell>
          <StyledTableBodyCell>{service.serviceTypeName}</StyledTableBodyCell>
          <StyledTableBodyCell>{service.calcTypeName}</StyledTableBodyCell>
          <StyledTableBodyCell>{service.supplierName}</StyledTableBodyCell>
          <StyledTableBodyCell>{service.percentOrValue}</StyledTableBodyCell>
          <StyledTableBodyCell>{service.periodicity}</StyledTableBodyCell>
          <StyledTableBodyCell>
            <>{(service.financedYN) === true ? <Check color='success' /> : <Close />}</>
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <>{(service.implicit) === true ? <Check color='success' /> : <Close />}</>
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <>{(service.mandatory) === true ? <Check color='success' /> : <Close />}</>
          </StyledTableBodyCell>
          <StyledTableBodyCell align='right'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton
                type='edit'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Edit')}
                onClick={() => set(serviceLens.isInEditing)(true)}
                disabled={!canWrite}
              />
              <IconButton
                type='delete'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Delete')}
                onClick={() => onDelete(service.financialProductRuleXServiceTypeId, index)}
                disabled={!canWrite}
              />
            </Grid>
          </StyledTableBodyCell>
        </>
      )
      }
    </StyledTableRow >
  );
};

FinancialProductRulesEditDetailsServicesTableRow.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  financialProductRuleServiceTypeList: PropTypes.array.isRequired,
  financialProductRuleServiceDetailCalcTypeList: PropTypes.array.isRequired,
  financialProductRuleSupplierList: PropTypes.array.isRequired,
  serviceLens: PropTypes.object,
  index: PropTypes.number,
  onCloseEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onLoadFinancialProductRuleSupplierList: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  listValid: PropTypes.bool.isRequired
};

export default FinancialProductRulesEditDetailsServicesTableRow;
